import { appStoreConstant } from "@/utils/branch"
import clsx from "clsx"
import { useState } from "react"
import { z } from "zod"
import Bubble from "../bubble"
import Button from "../button"

const featureAiToolSchema = z.enum([
	"deform",
	"ai_photo",
	"ai_restyle",
	"text_to_image",
	"text_to_video",
	"ai_music_generator",
])

const benefitSchema = z.object({
	title: z.string(),
	text: z.string(),
	media: z.string(),
	alt: z.string().optional(),
})

export const benefitsSchema = z.object({
	component: z.literal("benefits"),
	head_title: z.string(),
	button_link: z.string().optional(),
	button_text: z.string().optional(),
	bubble_text: z.string(),
	benefits_list: z.array(benefitSchema),
	upload: z.boolean().optional(),
	tool: featureAiToolSchema.optional(),
})

type Benefits = z.infer<typeof benefitSchema>[]
type Benefit = z.infer<typeof benefitSchema>
type BenefitsProps = z.infer<typeof benefitsSchema>

export default function Benefits(props: BenefitsProps) {
	const {
		head_title,
		benefits_list,
		button_link,
		button_text,
		bubble_text,
	} = props

	return (
		<>
			<div className="flex flex-col gap-8 py-[35px] tablet:py-[40px] desktop:items-center desktop:py-[55px]">
				<div className="flex w-full flex-col items-center gap-[22px]">
					<div className="flex flex-row justify-center">
						<Bubble text={bubble_text} color="purplePink" />
					</div>
					<h2 className="text-h3 px-4 text-center desktop:max-w-[900px]">
						{head_title}
					</h2>
				</div>
				<div className="no-scrollbar flex flex-row items-start gap-[34px] overflow-x-scroll px-4 tablet:justify-center tablet:gap-[20px] desktop:gap-[90px]">
					{benefits_list.map((benefit, index) => (
						<SingleBenefit
							key={index}
							benefit={benefit}
							image={benefit.media}
						/>
					))}
				</div>
				{button_text && (
					<div className="flex items-center justify-center">
						<Button
							href={button_link}
							className="mt-8 hidden tablet:mt-0 tablet:!w-[275px] desktop:block"
							text={button_text}
							primary
							full
						/>
						<Button
							href={appStoreConstant}
							text={button_text}
							primary
							full
							className=" tablet:mt-0 tablet:!w-[275px] desktop:hidden"
						/>
					</div>
				)}
			</div>
		</>
	)
}

function SingleBenefit(props: {
	benefit: Benefit
	image: string
}) {
	const { benefit, image } = props
	const { title, text, alt } = benefit

	const [seeMore, setSeeMore] = useState(false)

	return (
		<>
			<div className="flex w-[226px] shrink-0 flex-col items-center justify-center gap-[16px] text-center desktop:w-[302px]">
				<img
					src={image}
					alt={alt ?? title}
					className="ah-[233px] w-full rounded-2xl object-cover desktop:h-[191px]"
				/>
				<div className="flex flex-col gap-2 px-[3px]">
					<p className="text-h6 text-left">{title}</p>
					<p
						className={clsx(
							"text-body-4 text-left text-blue-700 transition-all",
							seeMore ? "line-clamp-none" : "line-clamp-3",
						)}>
						{text}
					</p>
					<button
						onClick={() => setSeeMore(!seeMore)}
						className={clsx(
							"text-body-4 text-blue-600",
							text.length < 105 && "hidden",
						)}>
						{seeMore ? "less" : "see more"}
					</button>
				</div>
			</div>
		</>
	)
}
